import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useService } from '../../../../containers/UseService.js';
import { useAppSnackbars } from '../../../../hooks/utils/useAppSnackbars.js';
import { newCampaignActions } from '../../../../store';
import {
    extractFileProperties,
    getRemoteVideoDuration,
    resizeImageFile,
} from '../../../../utils/functions.js';
import {
    resizeVideoFile,
    trimVideoFile,
} from '../../../../utils/trimVideoFile.js';
import {
    exceedsGroupMaxWeight,
    formatBytesAsMb,
    getGroupMaxWeightBytes,
    getScreensMaxWeights,
} from './creativeWeight.js';
import { RequiredTag } from '../../../Input/RequiredTag.js';
import FileCard from './Components/FileCard';
import GroupContainer from './Components/GroupContainer.js';
import UploadCard from './Components/UploadCard';
import UploadZone from './Components/UploadZone';
import DynamicUrlModal from './Components/DynamicUrlModal';
import {
    DYNAMIC_URL_SOURCE,
    getUrlCreativeName,
    isDynamicUrlCreative,
    isScreenAllowedForCreative,
} from './dynamicUrlCreatives.js';

const debugVideo = (stage, details) => {
    globalThis.console.info('[creative-video]', stage, details ?? '');
};

// Limite de uploads concurrentes para evitar saturar el backend o la conexión
const MAX_CONCURRENT_UPLOADS = 3;
// Re-encoding pads the audio/container by a few milliseconds; a larger drift
// means ffmpeg produced a partial file.
export const RESIZE_DURATION_TOLERANCE_S = 0.1;

const SCREEN_IDENTIFIER_FIELDS = ['external_id', 'externalId', 'key', 'id'];

const normalizeScreenIdentifier = (value) =>
    typeof value === 'string' || typeof value === 'number'
        ? String(value).trim().toLowerCase()
        : '';

const getScreenIdentifierValues = (screen) => {
    if (!screen || typeof screen !== 'object') return [];

    const values = [];

    const addValue = (value) => {
        if (value === null || value === undefined) return;
        const stringValue =
            typeof value === 'string' ? value.trim() : String(value);
        if (!stringValue) return;
        if (!values.includes(stringValue)) values.push(stringValue);
    };

    const providerId =
        typeof screen?.provider === 'object' ? screen?.provider?.id : undefined;
    const rawId =
        screen?.id !== null && screen?.id !== undefined
            ? screen?.id
            : undefined;

    if (
        providerId &&
        rawId !== undefined &&
        rawId !== null &&
        String(rawId).trim()
    ) {
        const rawIdString = String(rawId).trim();
        if (!rawIdString.startsWith(`${providerId}_`)) {
            addValue(`${providerId}_${rawIdString}`);
        }
    }

    for (const field of SCREEN_IDENTIFIER_FIELDS) {
        addValue(screen[field]);
    }

    return values;
};

const getScreenIdentifier = (screen) =>
    getScreenIdentifierValues(screen)[0] ?? null;

const getNormalizedScreenIdentifiers = (screen) =>
    getScreenIdentifierValues(screen)
        .map((identifier) => normalizeScreenIdentifier(identifier))
        .filter(Boolean);

// A dynamic URL creative pinned to a screen only fits the group holding it.
const isPinnedScreenInGroup = (creativeProperties, group) =>
    !isDynamicUrlCreative(creativeProperties) ||
    !creativeProperties.pinnedScreenId ||
    (group?.screens ?? []).some((screen) =>
        isScreenAllowedForCreative(creativeProperties, screen)
    );

const buildGroupsLinkSnapshot = (groups = []) =>
    JSON.stringify(
        groups
            .map((group) => ({
                hash: group.hash,
                screenIds: (group.screens ?? [])
                    .map(getScreenIdentifier)
                    .filter(Boolean)
                    .map(String)
                    .sort((a, b) => String(a).localeCompare(String(b))),
                creativeHashes: (group.assigned_creatives ?? [])
                    .map((creative) => creative.properties?.hash)
                    .filter(Boolean)
                    .sort((a, b) => String(a).localeCompare(String(b))),
            }))
            .sort((a, b) => String(a.hash).localeCompare(String(b.hash)))
    );

const buildPersistedGroupsLinkSnapshot = (groups = [], filesMap = new Map()) =>
    JSON.stringify(
        groups
            .map((group) => {
                const groupScreenIds = (group.screens ?? [])
                    .map(getScreenIdentifier)
                    .filter(Boolean)
                    .map(String);
                const normalizedGroupScreenIdentifiers = new Set(
                    (group.screens ?? []).flatMap((screen) =>
                        getNormalizedScreenIdentifiers(screen)
                    )
                );

                const persistedCreatives = Array.from(filesMap.values()).filter(
                    (creative) => {
                        const linkedScreenIds =
                            getNormalizedLinkedScreenIds(creative);

                        return (
                            groupScreenIds.length > 0 &&
                            linkedScreenIds.length > 0 &&
                            linkedScreenIds.some((screenId) =>
                                normalizedGroupScreenIdentifiers.has(screenId)
                            )
                        );
                    }
                );

                return {
                    hash: group.hash,
                    screenIds: groupScreenIds.sort((a, b) =>
                        String(a).localeCompare(String(b))
                    ),
                    creativeHashes: persistedCreatives
                        .map((creative) => creative.properties?.hash)
                        .filter(Boolean)
                        .sort((a, b) => String(a).localeCompare(String(b))),
                };
            })
            .sort((a, b) => String(a.hash).localeCompare(String(b.hash)))
    );

const CREATIVE_DIMENSION_TOLERANCE_PX = 3;
export const getVideoDurationEligibility = (duration, slotDuration) => {
    const videoDuration = Number(duration);
    const targetDuration = Number(slotDuration);
    if (!Number.isFinite(videoDuration) || !Number.isFinite(targetDuration)) {
        return 'rejected';
    }

    const excess = videoDuration - targetDuration;
    if (excess === 0) return 'exact';
    if (excess > 0 && Math.floor(videoDuration) === targetDuration) {
        return 'trimmable';
    }
    return 'rejected';
};

const isWithinTolerance = (value, target) =>
    Math.abs(value - target) <= CREATIVE_DIMENSION_TOLERANCE_PX;

const isFileSizeWithinTolerance = (fileProperties, group) =>
    isWithinTolerance(fileProperties.width, group.width) &&
    isWithinTolerance(fileProperties.height, group.height);

const isFileSizeExactMatch = (fileProperties, group) =>
    Number(fileProperties.width) === Number(group.width) &&
    Number(fileProperties.height) === Number(group.height);

const getCreativeAssignmentDebugInfo = (fileProperties, group) => {
    const reasons = [];

    if (!fileProperties) {
        reasons.push('missing file properties');
        return { matches: false, reasons };
    }

    if (!group) {
        reasons.push('missing target group');
        return { matches: false, reasons };
    }

    if (
        (group.motion_type === 'STILL' && fileProperties.type === 'video') ||
        (group.motion_type === 'STILL' &&
            fileProperties.type === 'image' &&
            fileProperties.duration > 0)
    ) {
        reasons.push('group is STILL, this creative is not allowed');
    }

    if (
        fileProperties.type !== 'dynamic' &&
        !isFileSizeExactMatch(fileProperties, group)
    ) {
        reasons.push(
            `size mismatch: file=${fileProperties.width}x${fileProperties.height}, group=${group.width}x${group.height}`
        );
    }

    if (
        fileProperties.type === 'video' &&
        getVideoDurationEligibility(
            fileProperties.duration,
            group.slot_duration
        ) !== 'exact'
    ) {
        reasons.push(
            `duration mismatch: file=${fileProperties.duration}, group=${group.slot_duration}`
        );
    }

    if (fileProperties.type === 'dynamic' && group.player?.id !== 1) {
        reasons.push(
            `dynamic creative not supported by player ${group.player?.id}`
        );
    }

    if (!isPinnedScreenInGroup(fileProperties, group)) {
        reasons.push(
            `dynamic URL screen ${fileProperties.pinnedScreenId} not in group`
        );
    }

    if (exceedsGroupMaxWeight(group, fileProperties)) {
        reasons.push(
            `weight exceeded: file=${formatBytesAsMb(fileProperties.size)}, max=${formatBytesAsMb(getGroupMaxWeightBytes(group, fileProperties))}`
        );
    }

    return {
        matches: reasons.length === 0,
        reasons,
    };
};

export const isCreativeCompatibleWithGroup = (group, creativeProperties) => {
    if (!group || !creativeProperties) {
        return false;
    }

    if (
        group.motion_type === 'STILL' &&
        (creativeProperties.type === 'video' ||
            (creativeProperties.type === 'image' &&
                creativeProperties.duration > 0))
    ) {
        return false;
    }

    if (
        creativeProperties.type !== 'dynamic' &&
        !isFileSizeExactMatch(creativeProperties, group)
    ) {
        return false;
    }

    if (
        creativeProperties.type === 'video' &&
        getVideoDurationEligibility(
            creativeProperties.duration,
            group.slot_duration
        ) !== 'exact'
    ) {
        return false;
    }

    if (creativeProperties.type === 'dynamic' && group.player?.id !== 1) {
        return false;
    }

    if (!isPinnedScreenInGroup(creativeProperties, group)) {
        return false;
    }

    if (exceedsGroupMaxWeight(group, creativeProperties)) {
        return false;
    }

    return true;
};

const getCreativeDisplayName = (creative, t) => {
    const remoteFileName = creative?.remoteUrl
        ?.split('?')[0]
        ?.split('/')
        ?.pop();

    return (
        creative?.file?.name ||
        creative?.properties?.name ||
        creative?.properties?.filename ||
        remoteFileName ||
        creative?.properties?.hash ||
        t(
            'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.unnamed-creative'
        )
    );
};

const buildLinkRequestFromGroups = (groups = [], filesMap = new Map()) => {
    const map = new Map();

    for (const creative of filesMap.values()) {
        const creativeId = creative?.creativeId;

        if (!creativeId) {
            continue;
        }

        map.set(String(creativeId), new Set());
    }

    for (const group of groups) {
        for (const creative of group.assigned_creatives ?? []) {
            const creativeHash = creative.properties?.hash;
            const creativeId =
                creative.creativeId || filesMap.get(creativeHash)?.creativeId;

            if (!creativeId) {
                continue;
            }

            const normalizedCreativeId = String(creativeId);

            if (!map.has(normalizedCreativeId)) {
                map.set(normalizedCreativeId, new Set());
            }

            const creativeProperties =
                creative.properties ?? filesMap.get(creativeHash)?.properties;
            const linkedScreens = map.get(normalizedCreativeId);
            (group.screens ?? [])
                .filter((screen) =>
                    isScreenAllowedForCreative(creativeProperties, screen)
                )
                .map(getScreenIdentifier)
                .filter(Boolean)
                .forEach((screenId) => linkedScreens.add(screenId));
        }
    }

    return Array.from(map.entries()).map(([id, screenIds]) => ({
        id,
        screenIds: Array.from(screenIds),
    }));
};

const buildAssignedCreativeFromFileItem = (creativeItem) => ({
    file: creativeItem?.file ?? null,
    properties: creativeItem?.properties,
    remoteUrl: creativeItem?.remoteUrl,
    localPreviewUrl: creativeItem?.localPreviewUrl,
    creativeId: creativeItem?.creativeId,
    upload: creativeItem?.upload,
});

const applyScreenIdsToFilesMap = (filesMap, linkRequest = []) => {
    const nextFiles = new Map(filesMap);
    const screenIdsByCreativeId = new Map(
        linkRequest.map((item) => [String(item.id), item.screenIds ?? []])
    );

    for (const [hash, creative] of nextFiles.entries()) {
        const creativeId = creative?.creativeId
            ? String(creative.creativeId)
            : null;

        nextFiles.set(hash, {
            ...creative,
            screenIds: creativeId
                ? (screenIdsByCreativeId.get(creativeId) ?? [])
                : (creative.screenIds ?? []),
        });
    }

    return nextFiles;
};

const getLinkedScreenIds = (creativeItem) =>
    (creativeItem?.screenIds ?? []).filter(Boolean).map(String);

const getNormalizedLinkedScreenIds = (creativeItem) =>
    (creativeItem?.screenIds ?? [])
        .map((screenId) => normalizeScreenIdentifier(screenId))
        .filter(Boolean);

const getCompatibleGroups = (groups = [], creativeProperties) =>
    groups.filter((group) =>
        isCreativeCompatibleWithGroup(group, creativeProperties)
    );

const canFileFitGroupBySizeOnly = (fileProperties, group) => {
    if (!fileProperties || !group) {
        return false;
    }

    if (group.motion_type === 'STILL' && fileProperties.type === 'video') {
        return false;
    }

    if (
        fileProperties.type !== 'dynamic' &&
        !isFileSizeWithinTolerance(fileProperties, group)
    ) {
        return false;
    }

    if (fileProperties.type === 'dynamic' && group.player?.id !== 1) {
        return false;
    }

    return true;
};

const canFileFitGroupForProcessing = (fileProperties, group) =>
    canFileFitGroupBySizeOnly(fileProperties, group) &&
    (fileProperties.type !== 'video' ||
        getVideoDurationEligibility(
            fileProperties.duration,
            group.slot_duration
        ) !== 'rejected');

const getGroupFitScore = (fileProperties, group) => {
    if (!canFileFitGroupForProcessing(fileProperties, group)) {
        return Number.POSITIVE_INFINITY;
    }

    if (fileProperties.type === 'dynamic') {
        return 0;
    }

    const durationPenalty =
        fileProperties.type === 'video' &&
        getVideoDurationEligibility(
            fileProperties.duration,
            group.slot_duration
        ) === 'trimmable'
            ? CREATIVE_DIMENSION_TOLERANCE_PX * 2 + 1
            : 0;

    return (
        durationPenalty +
        Math.abs(fileProperties.width - group.width) +
        Math.abs(fileProperties.height - group.height)
    );
};

const getGroupSizeMatchScore = (fileProperties, group) => {
    if (!canFileFitGroupBySizeOnly(fileProperties, group)) {
        return Number.POSITIVE_INFINITY;
    }

    if (fileProperties.type === 'dynamic') {
        return 0;
    }

    return (
        Math.abs(fileProperties.width - group.width) +
        Math.abs(fileProperties.height - group.height)
    );
};

const getVariantTargetKey = (fileProperties, group) => {
    const trimTo =
        fileProperties.type === 'video' &&
        getVideoDurationEligibility(
            fileProperties.duration,
            group.slot_duration
        ) === 'trimmable'
            ? Number(group.slot_duration)
            : 'source';

    return `${Number(group.width)}x${Number(group.height)}|${trimTo}`;
};

// One target group per distinct exact resolution (and trim duration) the
// source can be normalized to, best fit first.
export const getVariantTargets = (groups = [], fileProperties) => {
    if (!fileProperties || fileProperties.type === 'dynamic') {
        return [];
    }

    const targetsByKey = new Map();

    for (const group of groups) {
        if (!canFileFitGroupForProcessing(fileProperties, group)) {
            continue;
        }

        const key = getVariantTargetKey(fileProperties, group);
        const current = targetsByKey.get(key);

        if (
            !current ||
            getGroupFitScore(fileProperties, group) <
                getGroupFitScore(fileProperties, current)
        ) {
            targetsByKey.set(key, group);
        }
    }

    return [...targetsByKey.values()].sort(
        (groupA, groupB) =>
            getGroupFitScore(fileProperties, groupA) -
            getGroupFitScore(fileProperties, groupB)
    );
};

const findSizeMatchingGroup = (groups = [], fileProperties) =>
    [...groups]
        .filter((group) => canFileFitGroupBySizeOnly(fileProperties, group))
        .sort(
            (groupA, groupB) =>
                getGroupSizeMatchScore(fileProperties, groupA) -
                getGroupSizeMatchScore(fileProperties, groupB)
        )[0] ?? null;

export class VariantProcessingError extends Error {
    constructor(message, { kind, width, height, slotDuration, cause } = {}) {
        super(message);
        this.name = 'VariantProcessingError';
        this.kind = kind;
        this.width = width;
        this.height = height;
        this.slotDuration = slotDuration;
        this.cause = cause;
    }
}

const assertCompleteOutput = (outputProperties, sourceType) => {
    if (
        !outputProperties?.type ||
        !outputProperties?.hash ||
        (sourceType === 'video' && !Number.isFinite(outputProperties.duration))
    ) {
        throw new Error('Could not read the processed file metadata');
    }
};

const assertResizeKeptDuration = (outputProperties, sourceProperties) => {
    if (!Number.isFinite(sourceProperties.duration)) {
        return;
    }

    const drift = Math.abs(
        outputProperties.duration - sourceProperties.duration
    );
    if (drift > RESIZE_DURATION_TOLERANCE_S) {
        throw new Error(
            `Resized video lasts ${outputProperties.duration}s instead of ${sourceProperties.duration}s`
        );
    }
};

const assertExactGroupSize = (fileProperties, group) => {
    if (!isFileSizeExactMatch(fileProperties, group)) {
        throw new Error(
            `Output size ${fileProperties.width}x${fileProperties.height} does not match ${group.width}x${group.height}`
        );
    }
};

export const transformFileToMatchingGroup = async (
    file,
    fileProperties,
    group
) => {
    if (!file || !fileProperties || !group) {
        return { file, properties: fileProperties, resized: false, trimmed: false };
    }

    debugVideo('transform.start', {
        name: file.name,
        properties: fileProperties,
        group: {
            width: group.width,
            height: group.height,
            slotDuration: group.slot_duration,
        },
    });

    const needsDimensionNormalization =
        fileProperties.type !== 'dynamic' &&
        !isFileSizeExactMatch(fileProperties, group);
    const needsTrim =
        fileProperties.type === 'video' &&
        getVideoDurationEligibility(
            fileProperties.duration,
            group.slot_duration
        ) === 'trimmable';

    let stage = 'normalize';

    try {
        let transformedFile = file;
        let transformedProperties = fileProperties;
        let resized = false;
        let trimmed = false;

        if (needsDimensionNormalization) {
            if (!isFileSizeWithinTolerance(fileProperties, group)) {
                throw new Error('Creative size is outside the group tolerance');
            }

            transformedFile =
                fileProperties.type === 'video'
                    ? await resizeVideoFile(file, group.width, group.height, {
                          durationSeconds: fileProperties.duration,
                      })
                    : await resizeImageFile(file, group.width, group.height);
            const resizedProperties = await extractFileProperties(transformedFile);
            assertCompleteOutput(resizedProperties, fileProperties.type);
            transformedProperties = resizedProperties;
            if (fileProperties.type === 'video') {
                assertResizeKeptDuration(resizedProperties, fileProperties);
                // Keep the source duration so exact-slot sources stay exact
                // despite the encoder padding.
                if (Number.isFinite(fileProperties.duration)) {
                    transformedProperties = {
                        ...resizedProperties,
                        duration: fileProperties.duration,
                    };
                }
            }
            assertExactGroupSize(transformedProperties, group);
            resized = true;
        }

        if (needsTrim) {
            stage = 'trim';
            debugVideo('transform.trim.start', {
                name: transformedFile.name,
                duration: fileProperties.duration,
                targetDuration: group.slot_duration,
            });
            transformedFile = await trimVideoFile(
                transformedFile,
                group.slot_duration
            );
            transformedProperties = await extractFileProperties(transformedFile);
            assertCompleteOutput(transformedProperties, 'video');
            const slotDuration = Number(group.slot_duration);
            const outputDuration = Number(transformedProperties.duration);
            const durationDelta = Math.abs(outputDuration - slotDuration);

            if (durationDelta > 0) {
                globalThis.console.error(
                    '[creative-video] transform.trim.duration-mismatch',
                    {
                        name: transformedFile.name,
                        outputDuration,
                        slotDuration,
                        durationDelta,
                    }
                );
            }

            if (getVideoDurationEligibility(outputDuration, slotDuration) !== 'exact') {
                throw new Error('Trimmed video duration does not match the slot');
            }
            assertExactGroupSize(transformedProperties, group);
            debugVideo('transform.trim.success', {
                name: transformedFile.name,
                properties: transformedProperties,
            });
            trimmed = true;
        }

        return {
            file: transformedFile,
            properties: transformedProperties,
            resized,
            trimmed,
        };
    } catch (error) {
        debugVideo('transform.error', { name: file.name, error });
        throw new VariantProcessingError(
            fileProperties.type === 'video'
                ? 'Unable to reencode the video to match the selected screen size'
                : 'Unable to resize the creative to match the selected screen size',
            {
                kind: stage,
                width: group.width,
                height: group.height,
                slotDuration: group.slot_duration,
                cause: error,
            }
        );
    }
};

const CreativesContainer = forwardRef(function CreativesContainer(
    {
        selectedScreens,
        campaignPreviewId,
        isDirtyState,
        hasUnsavedSelectedScreens = false,
    },
    ref
) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const campaignName = useSelector((state) => state.newCampaign?.name);
    const campaignPreviewIdRef = useRef(campaignPreviewId);

    const [files, setFiles] = useState(new Map());
    const [screensGroups, setScreensGroups] = useState([]);

    const [lastLinkedGroupsSnapshot, setLastLinkedGroupsSnapshot] =
        useState('');

    const [filesIgnored, setFilesIgnored] = useState(0);
    const [isDynamicUrlModalOpen, setIsDynamicUrlModalOpen] = useState(false);

    const totalAssignedCreatives = useMemo(
        () =>
            screensGroups.reduce(
                (total, group) =>
                    total + (group.assigned_creatives?.length ?? 0),
                0
            ),
        [screensGroups]
    );

    const assignmentCountByCreativeHash = useMemo(() => {
        const map = new Map();
        for (const group of screensGroups) {
            for (const assigned of group.assigned_creatives ?? []) {
                const hash = assigned.properties?.hash;
                if (!hash) continue;
                map.set(hash, (map.get(hash) ?? 0) + 1);
            }
        }
        return map;
    }, [screensGroups]);

    const compatibleGroupsByCreativeHash = useMemo(() => {
        const map = new Map();

        for (const fileEntry of files.values()) {
            const hash = fileEntry.properties?.hash;
            if (!hash || !fileEntry.creativeId) continue;

            const groups = screensGroups
                .filter((group) =>
                    isCreativeCompatibleWithGroup(
                        group,
                        fileEntry.properties
                    )
                )
                .map((group) => {
                    const firstScreen = group.screens?.[0];

                    return {
                        hash: group.hash,
                        label: `${firstScreen?.width ?? group.width}x${
                            firstScreen?.height ?? group.height
                        } · ${firstScreen?.slot_duration ?? group.slot_duration}s`,
                        isCurrentlyAssigned: (group.assigned_creatives ?? []).some(
                            (c) => c.properties?.hash === hash
                        ),
                    };
                });

            map.set(hash, groups);
        }
        return map;
    }, [screensGroups, files]);

    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({
        groups: false,
        uploads: false,
    });
    const isHydratingCreativesRef = useRef(false);
    const ignorePersistedDirtyStateRef = useRef(false);

    const { openSuccess, openError } = useAppSnackbars();

    useEffect(() => {
        campaignPreviewIdRef.current = campaignPreviewId;
    }, [campaignPreviewId]);

    //remplazar sg por ref para evitar problemas de stale closure en funciones de upload/queue
    const screensGroupsRef = useRef(screensGroups);

    useEffect(() => {
        screensGroupsRef.current = screensGroups;
        if (touched.groups || touched.uploads) {
            const { errors: validationErrors } = validateCreatives();
            setErrors(validationErrors);
        }
    }, [
        screensGroups,
        lastLinkedGroupsSnapshot,
        isDirtyState,
        hasUnsavedSelectedScreens,
    ]);

    useEffect(() => {
        const currentSnapshot = buildGroupsLinkSnapshot(screensGroups);
        const externalDirty = ignorePersistedDirtyStateRef.current
            ? false
            : Boolean(isDirtyState);

        // Initialize
        if (!lastLinkedGroupsSnapshot && screensGroups.length > 0) {
            setLastLinkedGroupsSnapshot(currentSnapshot);
            dispatch(newCampaignActions.setCreativesDirty(externalDirty));
            return;
        }

        const hasChanges =
            externalDirty ||
            (Boolean(lastLinkedGroupsSnapshot) &&
                currentSnapshot !== lastLinkedGroupsSnapshot);

        dispatch(newCampaignActions.setCreativesDirty(hasChanges));

        if (ignorePersistedDirtyStateRef.current && !isDirtyState) {
            ignorePersistedDirtyStateRef.current = false;
        }
    }, [dispatch, screensGroups, lastLinkedGroupsSnapshot, isDirtyState]);

    function getAssignedCreativeId(creative) {
        return (
            creative.creativeId ||
            filesRef.current.get(creative.properties?.hash)?.creativeId
        );
    }

    function buildLinkRequest() {
        return buildLinkRequestFromGroups(
            screensGroupsRef.current,
            filesRef.current
        );
    }

    function hasAssignedCreativesWithoutId(groups = screensGroupsRef.current) {
        return groups.some((group) =>
            (group.assigned_creatives ?? []).some(
                (creative) => !getAssignedCreativeId(creative)
            )
        );
    }

    function hasUnlinkedCreativeChanges(groups = screensGroupsRef.current) {
        return (
            (!ignorePersistedDirtyStateRef.current && Boolean(isDirtyState)) ||
            (Boolean(lastLinkedGroupsSnapshot) &&
                buildGroupsLinkSnapshot(groups) !== lastLinkedGroupsSnapshot)
        );
    }

    function validateCreatives() {
        const nextErrors = {};
        const groups = screensGroupsRef.current ?? [];

        // 1) uploads pendientes o fallidos
        const uploadsError = getUploadsError(groups);
        if (uploadsError) {
            nextErrors.uploads = uploadsError;
        }

        // 2) grupos sin creatives
        const emptyGroupHashes = groups
            .filter((g) => (g.assigned_creatives ?? []).length === 0)
            .map((g) => g.hash);

        if (emptyGroupHashes.length > 0) {
            nextErrors.groups = {
                message: t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.groups-without-creatives'
                ),
                emptyGroupHashes,
            };
        }

        if (hasUnlinkedCreativeChanges(groups)) {
            nextErrors.links = t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-required'
            );
        }

        if (hasUnsavedSelectedScreens) {
            nextErrors.screens = t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-screens-required'
            );
        }

        const isValid = Object.keys(nextErrors).length === 0;
        return { isValid, errors: nextErrors };
    }

    function hasPendingUploads() {
        if (processingQueueRef.current.length > 0 || isProcessingRef.current) {
            return true;
        }
        for (const [, it] of filesRef.current.entries()) {
            const st = it?.upload?.status;
            if (
                st === 'waiting' ||
                st === 'queued' ||
                st === 'uploading' ||
                st === 'processing'
            )
                return true;
        }
        return false;
    }

    function hasFailedItems() {
        for (const [, it] of filesRef.current.entries()) {
            if (it?.upload?.status === 'error') return true;
        }
        return false;
    }

    // Blocks saving while any creative is unprocessed, not yet uploaded, or
    // failed: a failed item must be retried or removed by the user.
    function getUploadsError(groups = screensGroupsRef.current) {
        if (hasPendingUploads()) {
            return t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.pending-uploads'
            );
        }
        if (hasFailedItems()) {
            return t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.failed-creatives'
            );
        }
        if (hasAssignedCreativesWithoutId(groups)) {
            return t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.upload-errors'
            );
        }
        return null;
    }

    function validateLinksReadyForSave() {
        const nextErrors = {};

        const uploadsError = getUploadsError();
        if (uploadsError) {
            nextErrors.uploads = uploadsError;
        }

        setErrors(nextErrors);
        setTouched((prev) => ({
            ...prev,
            groups: false,
            uploads: true,
        }));

        return Object.keys(nextErrors).length === 0;
    }

    function markLinksPersisted(groups = screensGroupsRef.current) {
        const nextSnapshot = buildGroupsLinkSnapshot(groups);
        const linkRequest = buildLinkRequestFromGroups(
            groups,
            filesRef.current
        );
        const nextFiles = applyScreenIdsToFilesMap(
            filesRef.current,
            linkRequest
        );

        ignorePersistedDirtyStateRef.current = true;
        commitFiles(() => nextFiles);
        setLastLinkedGroupsSnapshot(nextSnapshot);
        setErrors((prev) => {
            const next = { ...prev };
            delete next.links;
            return next;
        });
        dispatch(newCampaignActions.setCreativesDirty(false));
    }

    function buildGroupsWithAutoAssignments(groups, filesMap) {
        const nextGroups = groups.map((group) => ({
            ...group,
            assigned_creatives: [...(group.assigned_creatives ?? [])],
        }));

        for (const creativeItem of filesMap.values()) {
            if (!creativeItem?.properties) {
                continue;
            }

            if (getLinkedScreenIds(creativeItem).length > 0) {
                continue;
            }

            const compatibleGroupHashes = new Set(
                getCompatibleGroups(nextGroups, creativeItem.properties).map(
                    (group) => group.hash
                )
            );

            nextGroups.forEach((group) => {
                if (!compatibleGroupHashes.has(group.hash)) {
                    return;
                }

                if (
                    group.assigned_creatives.some(
                        (assignedCreative) =>
                            assignedCreative.properties?.hash ===
                            creativeItem.properties.hash
                    )
                ) {
                    return;
                }

                group.assigned_creatives = [
                    ...group.assigned_creatives,
                    buildAssignedCreativeFromFileItem(creativeItem),
                ];
            });
        }

        return nextGroups;
    }

    function validate() {
        const { isValid, errors: nextErrors } = validateCreatives();

        setErrors(nextErrors);
        setTouched({ groups: true, uploads: true });
        return isValid;
    }

    useImperativeHandle(ref, () => ({
        buildLinkRequest,
        hasPendingUploads,
        hasAssignedCreativesWithoutId,
        validateLinksReadyForSave,
        markLinksPersisted,
        validate,
    }));

    const queueRef = useRef([]); // hashes pendientes
    // Processing jobs (resize/trim) run one at a time across drops.
    const processingQueueRef = useRef([]);
    const isProcessingRef = useRef(false);
    const inflightRef = useRef(new Set()); // hashes subiendo
    const filesRef = useRef(files); // mirror para leer estado actual sin delay

    // Single writer for the files map. The ref is the source of truth and is
    // updated synchronously, so reads between awaits never see a stale
    // snapshot and a full-map write cannot revert a functional one.
    function commitFiles(updater) {
        const next = updater(filesRef.current);
        if (next === filesRef.current) {
            return next;
        }
        filesRef.current = next;
        setFiles(next);
        return next;
    }

    useEffect(() => {
        return () => {
            for (const [, item] of filesRef.current.entries()) {
                if (item?.localPreviewUrl) {
                    try {
                        URL.revokeObjectURL(item.localPreviewUrl);
                    } catch {}
                }
            }
        };
    }, []);

    useEffect(() => {
        pumpQueue('files updated');
        if (touched.uploads) {
            const { errors: validationErrors } = validateCreatives();
            setErrors(validationErrors);
        }
    }, [files]);

    const service = useService();

    function hydrateAssignedCreativesFromFilesMap(filesMap) {
        setScreensGroups((prevGroups) => {
            const groupsWithPersistedAssignments = prevGroups.map((group) => {
                const groupScreenIdentifiers = new Set(
                    (group.screens ?? []).flatMap((screen) =>
                        getNormalizedScreenIdentifiers(screen)
                    )
                );

                const assignedCreatives = Array.from(filesMap.values()).filter(
                    (creativeItem) => {
                        const linkedScreenIds =
                            getNormalizedLinkedScreenIds(creativeItem);

                        return (
                            linkedScreenIds.length > 0 &&
                            groupScreenIdentifiers.size > 0 &&
                            linkedScreenIds.some((screenId) =>
                                groupScreenIdentifiers.has(screenId)
                            )
                        );
                    }
                );

                return {
                    ...group,
                    assigned_creatives: assignedCreatives.map((creativeItem) =>
                        buildAssignedCreativeFromFileItem(creativeItem)
                    ),
                };
            });

            const nextGroups = buildGroupsWithAutoAssignments(
                groupsWithPersistedAssignments,
                filesMap
            );

            if (isHydratingCreativesRef.current) {
                if (!isDirtyState) {
                    const persistedSnapshot = buildPersistedGroupsLinkSnapshot(
                        nextGroups,
                        filesMap
                    );
                    const currentSnapshot = buildGroupsLinkSnapshot(nextGroups);
                    setLastLinkedGroupsSnapshot(persistedSnapshot);
                    dispatch(
                        newCampaignActions.setCreativesDirty(
                            currentSnapshot !== persistedSnapshot
                        )
                    );
                }
                isHydratingCreativesRef.current = false;
            }

            return nextGroups;
        });
    }

    useEffect(() => {
        const previousGroupsByHash = new Map(
            (screensGroupsRef.current ?? []).map((group) => [group.hash, group])
        );
        const groups = [];

        selectedScreens.forEach((screen) => {
            const hashKey = `${screen.size}${screen.rotation}${
                screen.slot_duration
            }${screen.motion_type || 'MOVING'}`;

            const existing = groups.find((g) => g.hash === hashKey);

            if (!existing) {
                const previousGroup = previousGroupsByHash.get(hashKey);

                groups.push({
                    hash: hashKey,
                    width: screen.width,
                    height: screen.height,
                    rotation: screen.rotation,
                    slot_duration: screen.slot_duration,
                    motion_type: screen.motion_type,
                    player: screen.player,
                    screens: [screen],
                    assigned_creatives: [
                        ...(previousGroup?.assigned_creatives ?? []),
                    ],
                });
            } else {
                existing.screens.push(screen);
            }
        });

        groups.forEach((group) => {
            Object.assign(group, getScreensMaxWeights(group.screens));
        });

        screensGroupsRef.current = groups;
        setScreensGroups(groups);
    }, [selectedScreens]);

    useEffect(() => {
        if (
            filesRef.current.size === 0 ||
            screensGroupsRef.current.length === 0
        ) {
            return;
        }

        hydrateAssignedCreativesFromFilesMap(filesRef.current);
    }, [selectedScreens]);

    useEffect(() => {
        if (!campaignPreviewId) return;

        (async () => {
            const res = await service.getCreatives(campaignPreviewId);
            const list = res?.data ?? res;

            const entries = await Promise.all(
                list.map(async (ad) => {
                    let properties = {
                        ...(ad.properties ?? {}),
                        filename:
                            ad?.properties?.filename ||
                            ad?.properties?.name ||
                            ad?.original_filename ||
                            ad?.name,
                    };

                    // URL creatives are HTML5 ads without uploaded file. The
                    // backend keeps the pinned screen in title (empty when
                    // the URL is shared by every screen) and our name in
                    // original_filename.
                    if (properties?.type === 'dynamic' && ad?.image === '') {
                        const pinnedScreenId = ad?.title || undefined;
                        properties = {
                            ...properties,
                            name:
                                pinnedScreenId ||
                                ad?.original_filename ||
                                getUrlCreativeName(ad?.remoteUrl),
                            source: DYNAMIC_URL_SOURCE,
                            pinnedScreenId,
                        };
                    }

                    if (
                        properties?.type === 'video' &&
                        (!Number.isFinite(properties?.duration) ||
                            properties?.duration <= 0) &&
                        ad.remoteUrl
                    ) {
                        const recoveredDuration =
                            await getRemoteVideoDuration(ad.remoteUrl);

                        if (recoveredDuration) {
                            properties = {
                                ...properties,
                                duration: recoveredDuration,
                            };
                        }
                    }

                    return [
                        properties.hash,
                        {
                            file: undefined,
                            properties,
                            remoteUrl: ad.remoteUrl,
                            localPreviewUrl: undefined,
                            creativeId: ad.creativeId,
                            upload: ad.upload,
                            screenIds: ad.screenIds ?? [],
                        },
                    ];
                })
            );

            const map = new Map(entries);
            commitFiles(() => map);
            isHydratingCreativesRef.current = true;
            hydrateAssignedCreativesFromFilesMap(map);
        })().catch(console.error);
    }, [campaignPreviewId]);

    function enqueue(hash) {
        if (!queueRef.current.includes(hash)) {
            queueRef.current.push(hash);
        }
    }

    function pumpQueue(reason = 'manual') {
        let iterations = 0;
        const maxIterations = 100; // safety

        while (
            inflightRef.current.size < MAX_CONCURRENT_UPLOADS &&
            queueRef.current.length > 0 &&
            iterations++ < maxIterations
        ) {
            const hash = queueRef.current.shift();
            if (!hash) {
                continue;
            }

            if (inflightRef.current.has(hash)) {
                continue;
            }

            const item = filesRef.current.get(hash);
            if (!item) {
                // re-encolamos al final para intentar en el próximo tick/render
                queueRef.current.push(hash);
                continue;
            }

            if (item.upload?.status !== 'queued') {
                continue;
            }

            startUpload(hash);
        }
    }

    async function startUpload(hash) {
        inflightRef.current.add(hash);

        commitFiles((prev) => {
            const next = new Map(prev);
            const it = next.get(hash);
            if (!it) {
                return prev;
            }
            next.set(hash, {
                ...it,
                upload: { status: 'uploading', progress: 0 },
            });
            return next;
        });

        const itNow = filesRef.current.get(hash);
        const fileToUpload = itNow?.file;
        const keepLocalPreview = itNow?.properties?.type === 'video';

        try {
            if (!fileToUpload) throw new Error('Missing file to upload');
            const currentCampaignPreviewId = campaignPreviewIdRef.current;
            if (!currentCampaignPreviewId) {
                throw new Error('Missing campaign preview id');
            }

            const { creativeId, remoteUrl } = await service.uploadCreativeFile({
                file: fileToUpload,
                duration: itNow?.properties?.duration,
                campaignId: currentCampaignPreviewId,
                campaignName: campaignName || 'campaign',
            });

            commitFiles((prev) => {
                const next = new Map(prev);
                const it = next.get(hash);
                if (!it) return prev;

                if (it.localPreviewUrl && !keepLocalPreview) {
                    try {
                        URL.revokeObjectURL(it.localPreviewUrl);
                    } catch {}
                }

                next.set(hash, {
                    ...it,
                    creativeId,
                    remoteUrl,
                    localPreviewUrl: keepLocalPreview
                        ? it.localPreviewUrl
                        : undefined,
                    upload: { status: 'uploaded', progress: 1 },
                });
                return next;
            });
        } catch (e) {
            commitFiles((prev) => {
                const next = new Map(prev);
                const it = next.get(hash);
                if (!it) return prev;

                next.set(hash, {
                    ...it,
                    upload: {
                        status: 'error',
                        error: e?.message || String(e),
                        progress: 0,
                    },
                });
                return next;
            });
        } finally {
            inflightRef.current.delete(hash);

            pumpQueue('finally');
        }
    }

    function setUploadStatus(hash, status) {
        commitFiles((prev) => {
            const it = prev.get(hash);
            if (!it) return prev;
            const next = new Map(prev);
            next.set(hash, { ...it, upload: { status, progress: 0 } });
            return next;
        });
    }

    function isPlaceholderStatus(item) {
        return (
            item?.upload?.status === 'waiting' ||
            item?.upload?.status === 'processing'
        );
    }

    // Registers every accepted file of the drop as a `waiting` item and
    // queues its processing job. Processing runs later, one job at a time.
    async function insertFiles(droppedFiles) {
        let fileIgnoredCount = 0;
        debugVideo('drop.start', {
            count: droppedFiles.length,
            files: droppedFiles.map(({ name, size, type }) => ({
                name,
                size,
                type,
            })),
        });

        for (let i = 0; i < droppedFiles.length; i++) {
            const file = droppedFiles[i];
            debugVideo('file.analyze.start', { index: i, name: file.name });
            const fileProperties = await extractFileProperties(file);
            const groupsNow = screensGroupsRef.current;
            const variantTargets = getVariantTargets(groupsNow, fileProperties);
            debugVideo('file.analyze.success', {
                index: i,
                name: file.name,
                properties: fileProperties,
                variantTargets: variantTargets.map((group) => ({
                    width: group.width,
                    height: group.height,
                    slotDuration: group.slot_duration,
                    eligibility:
                        fileProperties?.type === 'video'
                            ? getVideoDurationEligibility(
                                  fileProperties.duration,
                                  group.slot_duration
                              )
                            : null,
                })),
            });
            const originalHash = fileProperties?.hash;

            if (fileProperties?.type === 'video' && variantTargets.length === 0) {
                const sizeMatchingGroup = findSizeMatchingGroup(
                    groupsNow,
                    fileProperties
                );

                if (sizeMatchingGroup) {
                    openError(
                        t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-duration-mismatch.message',
                            {
                                slotDuration: sizeMatchingGroup.slot_duration,
                            }
                        )
                    );
                }

                fileIgnoredCount++;
                debugVideo('file.rejected.no-matching-group', {
                    name: file.name,
                    properties: fileProperties,
                });
                continue;
            }

            // Dynamic creatives are not normalized: the source is the only
            // variant. Any other creative needs at least one target group.
            const targets =
                fileProperties?.type === 'dynamic' ? [null] : variantTargets;

            if (!originalHash || targets.length === 0) {
                fileIgnoredCount++;
                continue;
            }

            const existing = filesRef.current.get(originalHash);
            if (
                isPlaceholderStatus(existing) ||
                processingQueueRef.current.some(
                    (job) => job.originalHash === originalHash
                )
            ) {
                fileIgnoredCount++;
                debugVideo('file.rejected.already-processing', {
                    name: file.name,
                    originalHash,
                });
                continue;
            }

            // A source that is already listed (e.g. uploaded as is) is
            // processed again for new targets without a placeholder.
            let placeholderHash;
            if (!existing) {
                placeholderHash = originalHash;
                commitFiles((prev) => {
                    const next = new Map(prev);
                    next.set(originalHash, {
                        file,
                        properties: fileProperties,
                        localPreviewUrl: URL.createObjectURL(file),
                        remoteUrl: undefined,
                        creativeId: undefined,
                        upload: { status: 'waiting', progress: 0 },
                        abortController: undefined,
                    });
                    return next;
                });
            }

            processingQueueRef.current.push({
                originalHash,
                placeholderHash,
                file,
                fileProperties,
                targets,
            });
            debugVideo('file.waiting', { name: file.name, originalHash });
        }

        setFilesIgnored(fileIgnoredCount);
        debugVideo('drop.registered', {
            received: droppedFiles.length,
            ignored: fileIgnoredCount,
            queuedJobs: processingQueueRef.current.length,
        });
        await runProcessingQueue();
    }

    // Single-flight: concurrent drops only append jobs to the queue.
    async function runProcessingQueue() {
        if (isProcessingRef.current) {
            return;
        }

        isProcessingRef.current = true;
        try {
            while (processingQueueRef.current.length > 0) {
                const job = processingQueueRef.current.shift();
                try {
                    await processJob(job);
                } catch (error) {
                    console.error(error);
                    if (
                        job.placeholderHash &&
                        isPlaceholderStatus(
                            filesRef.current.get(job.placeholderHash)
                        )
                    ) {
                        commitProcessingFailure(job, null, error);
                    }
                }
            }
        } finally {
            isProcessingRef.current = false;
            // Re-render so validation sees the drained queue.
            commitFiles((prev) => new Map(prev));
        }
    }

    function isPlaceholderPresent(job) {
        return (
            !job.placeholderHash || filesRef.current.has(job.placeholderHash)
        );
    }

    async function processJob(job) {
        const { file, fileProperties, originalHash, placeholderHash, targets } =
            job;

        if (placeholderHash) {
            if (
                filesRef.current.get(placeholderHash)?.upload?.status !==
                'waiting'
            ) {
                return;
            }
            setUploadStatus(placeholderHash, 'processing');
        }

        debugVideo('file.processing.start', {
            name: file.name,
            targets: targets.length,
        });
        let placeholderHandedOff = false;
        let ignoredCount = 0;

        for (const target of targets) {
            const ownsPlaceholder = Boolean(
                placeholderHash && !placeholderHandedOff
            );
            let variant;

            try {
                variant = await processVariant({
                    file,
                    fileProperties,
                    target,
                    originalHash: ownsPlaceholder ? placeholderHash : undefined,
                });
            } catch (error) {
                if (!(error instanceof VariantProcessingError)) {
                    throw error;
                }

                debugVideo('file.processing.error', {
                    name: file.name,
                    error,
                });
                if (ownsPlaceholder && !isPlaceholderPresent(job)) {
                    return;
                }
                // The processed creative fits no group, or is too heavy for
                // its screens: nothing to retry, it is never uploaded.
                if (error.kind === 'assign' || error.kind === 'weight') {
                    notifyVariantError(error, fileProperties);
                    ignoredCount++;
                    continue;
                }
                commitProcessingFailure(job, target, error);
                continue;
            }

            // Removed by the user while processing: drop the result.
            if (ownsPlaceholder && !isPlaceholderPresent(job)) {
                if (variant.entry?.localPreviewUrl) {
                    URL.revokeObjectURL(variant.entry.localPreviewUrl);
                }
                return;
            }

            if (variant.duplicate) {
                ignoredCount++;
                continue;
            }

            const { hash, entry, resized, trimmed } = variant;

            const candidateGroups = buildGroupsWithAssignedCreative(
                screensGroupsRef.current,
                entry
            );
            screensGroupsRef.current = candidateGroups.nextGroups;
            setScreensGroups(candidateGroups.nextGroups);

            const errorKey = target
                ? getProcessingErrorKey(originalHash, fileProperties, target)
                : undefined;
            commitFiles((prev) => {
                const next = new Map(prev);
                if (ownsPlaceholder) {
                    const placeholder = next.get(placeholderHash);
                    if (placeholder?.localPreviewUrl) {
                        URL.revokeObjectURL(placeholder.localPreviewUrl);
                    }
                    if (placeholderHash !== hash) {
                        next.delete(placeholderHash);
                    }
                }
                // A successful re-drop replaces an earlier failure.
                const previousError = errorKey && next.get(errorKey);
                if (previousError?.upload?.status === 'error') {
                    if (previousError.localPreviewUrl) {
                        URL.revokeObjectURL(previousError.localPreviewUrl);
                    }
                    next.delete(errorKey);
                }
                next.set(hash, {
                    ...entry,
                    remoteUrl: undefined,
                    creativeId: undefined,
                    upload: { status: 'queued', progress: 0 },
                    abortController: undefined,
                });
                return next;
            });
            if (ownsPlaceholder) {
                placeholderHandedOff = true;
            }
            enqueue(hash);
            pumpQueue('insertFiles');
            debugVideo('file.processing.queued', {
                name: entry.file.name,
                properties: entry.properties,
                trimmed,
                resized,
            });

            if ((resized || trimmed) && target) {
                const normalizedNotificationKey = trimmed
                    ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-trimmed.success'
                    : entry.properties.type === 'video'
                      ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-normalized.success'
                      : 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.image-normalized.success';

                openSuccess(
                    t(normalizedNotificationKey, {
                        width: target.width,
                        height: target.height,
                        duration: target.slot_duration,
                    })
                );
            }
        }

        if (placeholderHash && !placeholderHandedOff) {
            const placeholder = filesRef.current.get(placeholderHash);
            if (isPlaceholderStatus(placeholder)) {
                removeFileLocally(placeholderHash, placeholder);
            }
        }
        if (ignoredCount > 0) {
            setFilesIgnored((count) => count + ignoredCount);
        }
    }

    function getProcessingErrorKey(originalHash, fileProperties, target) {
        return `${originalHash}:${getVariantTargetKey(fileProperties, target)}`;
    }

    function getProcessingErrorMessage(fileProperties, target, error) {
        const key =
            error?.kind === 'trim'
                ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-trimmed.error'
                : fileProperties.type === 'video'
                  ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-normalized.error'
                  : 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.image-normalized.error';

        return t(key, {
            width: target?.width,
            height: target?.height,
            duration: target?.slot_duration,
        });
    }

    // Lists a failed variant as an item in error. It keeps the source only to
    // retry the processing: it is never queued for upload or assigned.
    // `target === null` marks a failure of the whole job.
    function commitProcessingFailure(job, target, error) {
        const { file, fileProperties, originalHash } = job;
        const targets = target ? [target] : job.targets;
        const key = target
            ? getProcessingErrorKey(originalHash, fileProperties, target)
            : job.placeholderHash;

        commitFiles((prev) => {
            const next = new Map(prev);
            const current = next.get(key);
            if (current?.localPreviewUrl) {
                URL.revokeObjectURL(current.localPreviewUrl);
            }
            next.set(key, {
                file,
                properties: { ...fileProperties, hash: key },
                localPreviewUrl: URL.createObjectURL(file),
                remoteUrl: undefined,
                creativeId: undefined,
                upload: {
                    status: 'error',
                    stage: 'processing',
                    progress: 0,
                    error: error?.cause?.message || error?.message,
                    message: getProcessingErrorMessage(
                        fileProperties,
                        targets[0],
                        error
                    ),
                },
                retryJob: { originalHash, file, fileProperties, targets },
                abortController: undefined,
            });
            return next;
        });
    }

    // Produces one variant of a dropped file for a target group (or the
    // source itself when target is null). Throws VariantProcessingError when
    // the variant cannot be produced or assigned; callers decide how to
    // surface it.
    async function processVariant({
        file,
        fileProperties,
        target,
        originalHash,
    }) {
        let variantFile = file;
        let variantProperties = fileProperties;
        let resized = false;
        let trimmed = false;

        if (target) {
            const transformed = await transformFileToMatchingGroup(
                file,
                fileProperties,
                target
            );
            variantFile = transformed.file;
            variantProperties = transformed.properties;
            resized = transformed.resized;
            trimmed = transformed.trimmed;
        }

        // `size` is the weight of the file that will be uploaded (after any
        // resize/trim), checked against the target screens' max weight.
        const properties = {
            ...variantProperties,
            filename: file.name,
            size: variantFile.size,
        };
        const hash = properties.hash;

        if (filesRef.current.has(hash) && hash !== originalHash) {
            return { duplicate: true, hash };
        }

        if (target && exceedsGroupMaxWeight(target, properties)) {
            const error = new VariantProcessingError(
                'Creative exceeds the screen max weight',
                {
                    kind: 'weight',
                    width: target.width,
                    height: target.height,
                    slotDuration: target.slot_duration,
                }
            );
            error.fileWeight = formatBytesAsMb(properties.size);
            error.maxWeight = formatBytesAsMb(
                getGroupMaxWeightBytes(target, properties)
            );
            throw error;
        }

        if (
            getCompatibleGroups(screensGroupsRef.current, properties).length ===
            0
        ) {
            const assignmentInfo = getCreativeAssignmentDebugInfo(
                properties,
                target
            );
            const error = new VariantProcessingError(
                'Creative is not compatible with any group',
                {
                    kind: 'assign',
                    width: target?.width,
                    height: target?.height,
                    slotDuration: target?.slot_duration,
                }
            );
            error.reasons = assignmentInfo.reasons;
            error.resized = resized;
            error.creativeType = properties.type;
            throw error;
        }

        return {
            duplicate: false,
            hash,
            entry: {
                file: variantFile,
                properties,
                localPreviewUrl: URL.createObjectURL(variantFile),
            },
            resized,
            trimmed,
        };
    }

    function notifyVariantError(error, fileProperties) {
        if (error.kind === 'weight') {
            openError(
                t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.creative-weight-exceeded.message',
                    {
                        fileWeight: error.fileWeight,
                        maxWeight: error.maxWeight,
                        width: error.width,
                        height: error.height,
                        duration: error.slotDuration,
                    }
                )
            );
            return;
        }

        if (error.kind === 'assign') {
            const isVideo = error.creativeType === 'video';
            const prefix = isVideo
                ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-not-auto-assigned'
                : 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.creative-not-auto-assigned';

            openError(
                error.reasons?.length
                    ? t(`${prefix}.message-matched`, {
                          reasons: error.reasons.join('; '),
                      })
                    : isVideo
                      ? t(
                            error.resized
                                ? `${prefix}.message-resized`
                                : `${prefix}.message-no-resize`
                        )
                      : t(`${prefix}.message-default`)
            );
            return;
        }

        const normalizedNotificationKey =
            error.kind === 'trim'
                ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-trimmed.error'
                : fileProperties.type === 'video'
                  ? 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.video-normalized.error'
                  : 'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.image-normalized.error';

        openError(
            t(normalizedNotificationKey, {
                width: error.width,
                height: error.height,
                duration: error.slotDuration,
            })
        );
    }

    function buildGroupsWithAssignedCreative(groups, file) {
        let assigned = false;

        const compatibleGroupHashes = new Set(
            getCompatibleGroups(groups, file.properties).map(
                (group) => group.hash
            )
        );

        const nextGroups = groups.map((group) => {
            const nextGroup = {
                ...group,
                assigned_creatives: [...(group.assigned_creatives ?? [])],
            };

            if (!compatibleGroupHashes.has(nextGroup.hash)) {
                return nextGroup;
            }

            if (
                nextGroup.assigned_creatives.findIndex(
                    (assignedCreative) =>
                        assignedCreative.properties.hash ===
                        file.properties.hash
                ) < 0
            ) {
                assigned = true;
                nextGroup.assigned_creatives = [
                    ...nextGroup.assigned_creatives,
                    file,
                ];
            }

            return nextGroup;
        });

        return { assigned, nextGroups };
    }

    function AutoAssignCreatives(file) {
        const { assigned, nextGroups } = buildGroupsWithAssignedCreative(
            screensGroupsRef.current,
            file
        );

        // Keep the ref in sync so consecutive assignments in the same drop
        // build on the latest group state instead of overwriting prior ones.
        screensGroupsRef.current = nextGroups;
        setScreensGroups(nextGroups);
        return assigned;
    }

    function toggleCreativeInGroups(creativeHash, groupHash) {
        if (hasUnsavedSelectedScreens) {
            setErrors((prev) => ({
                ...prev,
                screens: t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-screens-required'
                ),
            }));
            setTouched({ groups: true, uploads: true });
            return false;
        }

        const creativeItem = filesRef.current.get(creativeHash);
        if (!creativeItem?.properties || !creativeItem.creativeId) {
            return false;
        }

        const targetGroup = screensGroupsRef.current.find(
            (group) => group.hash === groupHash
        );

        const isAssigned = (targetGroup?.assigned_creatives ?? []).some(
            (c) => c.properties?.hash === creativeHash
        );

        if (isAssigned) {
            const nextGroups = screensGroupsRef.current.map((group) => {
                if (group.hash !== groupHash) return group;
                return {
                    ...group,
                    assigned_creatives: (group.assigned_creatives ?? []).filter(
                        (c) => c.properties?.hash !== creativeHash
                    ),
                };
            });
            screensGroupsRef.current = nextGroups;
            setScreensGroups(nextGroups);
            return true;
        }

        return assignCreativeToGroup(groupHash, creativeHash);
    }

    function assignCreativeToAllGroups(creativeHash) {
        if (hasUnsavedSelectedScreens) {
            setErrors((prev) => ({
                ...prev,
                screens: t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-screens-required'
                ),
            }));
            setTouched({ groups: true, uploads: true });
            return false;
        }

        const creativeItem = filesRef.current.get(creativeHash);
        if (!creativeItem?.properties || !creativeItem.creativeId) {
            return false;
        }

        let changed = false;
        let nextGroups = screensGroupsRef.current;

        for (const group of screensGroupsRef.current) {
            if (
                !isCreativeCompatibleWithGroup(group, creativeItem.properties)
            ) {
                continue;
            }

            const alreadyAssigned = (group.assigned_creatives ?? []).some(
                (c) => c.properties?.hash === creativeHash
            );

            if (alreadyAssigned) continue;

            nextGroups = nextGroups.map((g) => {
                if (g.hash !== group.hash) return g;
                return {
                    ...g,
                    assigned_creatives: [...(g.assigned_creatives ?? []), creativeItem],
                };
            });
            changed = true;
        }

        if (changed) {
            screensGroupsRef.current = nextGroups;
            setScreensGroups(nextGroups);
        }

        return changed;
    }

    function assignCreativeToGroup(groupHash, creativeHash) {
        if (hasUnsavedSelectedScreens) {
            setErrors((prev) => ({
                ...prev,
                screens: t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-screens-required'
                ),
            }));
            setTouched({ groups: true, uploads: true });
            return false;
        }

        const creativeItem = filesRef.current.get(creativeHash);

        if (!creativeItem?.properties || !creativeItem.creativeId) {
            return false;
        }

        let changed = false;
        const nextGroups = screensGroupsRef.current.map((group) => {
            if (group.hash !== groupHash) {
                return group;
            }

            const nextGroup = {
                ...group,
                assigned_creatives: [...(group.assigned_creatives ?? [])],
            };

            if (
                !isCreativeCompatibleWithGroup(
                    nextGroup,
                    creativeItem.properties
                )
            ) {
                return nextGroup;
            }

            if (
                nextGroup.assigned_creatives.some(
                    (creative) =>
                        creative.properties?.hash ===
                        creativeItem.properties.hash
                )
            ) {
                return nextGroup;
            }

            changed = true;
            nextGroup.assigned_creatives = [
                ...nextGroup.assigned_creatives,
                buildAssignedCreativeFromFileItem(creativeItem),
            ];

            return nextGroup;
        });

        if (!changed) {
            return false;
        }

        screensGroupsRef.current = nextGroups;
        setScreensGroups(nextGroups);

        return true;
    }

    function UnassignCreatives(hash) {
        setScreensGroups((prevGroups) =>
            prevGroups.map((g) => ({
                ...g,
                assigned_creatives: (g.assigned_creatives ?? []).filter(
                    (f) => f.properties.hash !== hash
                ),
            }))
        );
    }

    function UnassignCreativeFromGroup(groupHash, creativeHash) {
        setScreensGroups((prevGroups) =>
            prevGroups.map((group) => {
                if (group.hash !== groupHash) {
                    return group;
                }

                return {
                    ...group,
                    assigned_creatives: (group.assigned_creatives ?? []).filter(
                        (creative) => creative.properties?.hash !== creativeHash
                    ),
                };
            })
        );
    }

    function removeFileLocally(hash, creativeItem) {
        const next = new Map(filesRef.current);

        if (creativeItem?.abortController) {
            try {
                creativeItem.abortController.abort();
            } catch {}
        }

        if (creativeItem?.localPreviewUrl) {
            try {
                URL.revokeObjectURL(creativeItem.localPreviewUrl);
            } catch {}
        }

        next.delete(hash);
        commitFiles(() => next);

        UnassignCreatives(hash);
        processingQueueRef.current = processingQueueRef.current.filter(
            (job) => job.placeholderHash !== hash
        );
        queueRef.current = queueRef.current.filter((h) => h !== hash);
        inflightRef.current.delete(hash);
    }

    async function removeFile(hash) {
        const creativeItem = filesRef.current.get(hash);
        const status = creativeItem?.upload?.status;

        if (status === 'uploaded') {
            const deleted = await service.removeCreative(
                creativeItem.creativeId
            );
            if (!deleted) {
                openError(
                    t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.uploaded-creatives.notifications.delete-warning.message'
                    )
                );
            }
            removeFileLocally(hash, creativeItem);
            return;
        }

        if (
            status === 'queued' ||
            status === 'waiting' ||
            status === 'processing' ||
            status === 'error' ||
            status === 'uploading'
        ) {
            removeFileLocally(hash, creativeItem);
            return;
        }
    }

    function retryUpload(hash) {
        commitFiles((prev) => {
            const next = new Map(prev);
            const it = next.get(hash);
            if (!it) return prev;
            next.set(hash, {
                ...it,
                upload: { status: 'queued', progress: 0, error: undefined },
            });
            return next;
        });
        enqueue(hash);
        pumpQueue('retry');
    }

    function retryItem(hash) {
        const item = filesRef.current.get(hash);
        if (item?.upload?.status !== 'error') {
            return;
        }

        if (item.upload.stage !== 'processing' || !item.retryJob) {
            retryUpload(hash);
            return;
        }

        // The error entry becomes the job's placeholder until it succeeds.
        setUploadStatus(hash, 'waiting');
        processingQueueRef.current.push({
            ...item.retryJob,
            placeholderHash: hash,
        });
        runProcessingQueue().catch(console.error);
    }

    // Creates the dynamic URL creatives and assigns them like zip creatives:
    // a pinned one only to its screen group, a shared one to every compatible
    // group. Links are saved with the rest of the creatives.
    async function createDynamicUrlCreatives(items) {
        const currentCampaignPreviewId = campaignPreviewIdRef.current;
        if (!currentCampaignPreviewId || items.length === 0) return false;

        let created;
        try {
            created = await service.createUrlCreatives({
                ads: items.map((item) => ({
                    url: item.url,
                    name: item.name,
                    ...(item.pinnedScreenId
                        ? { screen_id: item.pinnedScreenId }
                        : {}),
                })),
                duration: 0,
                campaignId: currentCampaignPreviewId,
                campaignName: campaignName || 'campaign',
            });
        } catch (e) {
            console.error(e);
            openError(
                t(
                    'sections.campaigns.new-campaign.wizard-creatives.creatives.dynamic-url-modal.errors.create-failed'
                )
            );
            return false;
        }

        const newItems = created.map(({ creativeId, remoteUrl }, index) => ({
            file: undefined,
            properties: {
                hash: `remote:${creativeId}`,
                type: 'dynamic',
                width: 0,
                height: 0,
                duration: 0,
                name: items[index].name,
                filename: items[index].name,
                source: DYNAMIC_URL_SOURCE,
                pinnedScreenId: items[index].pinnedScreenId,
            },
            remoteUrl,
            localPreviewUrl: undefined,
            creativeId,
            upload: { status: 'uploaded', progress: 1 },
            screenIds: [],
        }));

        commitFiles((prev) => {
            const next = new Map(prev);
            newItems.forEach((item) => next.set(item.properties.hash, item));
            return next;
        });

        let nextGroups = screensGroupsRef.current;
        newItems.forEach((item) => {
            nextGroups = buildGroupsWithAssignedCreative(
                nextGroups,
                buildAssignedCreativeFromFileItem(item)
            ).nextGroups;
        });
        screensGroupsRef.current = nextGroups;
        setScreensGroups(nextGroups);

        openSuccess(
            t(
                'sections.campaigns.new-campaign.wizard-creatives.creatives.dynamic-url-modal.created',
                { count: newItems.length }
            )
        );
        return true;
    }

    const validationMessages = [
        touched.uploads && errors.uploads,
        touched.groups && errors.screens,
        touched.groups && errors.groups?.message,
        touched.groups && errors.links,
    ].filter(Boolean);

    return (
        <div className="creatives flex justify-between content-center mt-2 h-full overflow-visible overflow-y-auto text-sm transition-transform duration-299">
            <div className="flex w-full m-5 overflow-visible overflow-y-auto text-sm transition-transform duration-299 flex-col space-y-8 pr-4">
                <div className="header flex flex-col w-full">
                    <div className="title text-xl font-extralight">
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.title'
                        )}
                    </div>
                    <div className="flex flex-row w-full space-x-4 mt-4">
                        <UploadCard
                            fileCount={files.size}
                            title={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-files-title'
                            )}
                            tooltipText={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-files-tooltip'
                            )}
                            processing={false}
                            supportedFiles={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-files-supported'
                            )}
                        />
                        <UploadCard
                            fileCount={totalAssignedCreatives}
                            title={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-assigned-title'
                            )}
                            tooltipText={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-assigned-tooltip'
                            )}
                            processing={false}
                            supportedFiles={t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-card-assigned-supported'
                            )}
                        />
                    </div>
                </div>
                <UploadZone
                    title={t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-zone-title'
                    )}
                    text={t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.upload-zone-text'
                    )}
                    canUpload={
                        Boolean(campaignPreviewId) && !hasUnsavedSelectedScreens
                    }
                    disabledMessage={
                        hasUnsavedSelectedScreens
                            ? t(
                                  'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-screens-before-upload'
                              )
                            : t(
                                  'sections.campaigns.new-campaign.wizard-creatives.creatives.validation.save-campaign-before-upload'
                              )
                    }
                    onDropFiles={async (droppedFiles) => {
                        await insertFiles(droppedFiles);
                    }}
                    filesIgnored={filesIgnored}
                    onAddDynamicUrl={() => setIsDynamicUrlModalOpen(true)}
                    addDynamicUrlLabel={t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.dynamic-url-modal.open-button'
                    )}
                />
                <DynamicUrlModal
                    isActive={isDynamicUrlModalOpen}
                    screens={selectedScreens}
                    onClose={() => setIsDynamicUrlModalOpen(false)}
                    onConfirm={createDynamicUrlCreatives}
                />
                <div className="title text-lg font-extralight">
                    {t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.title'
                    )}{' '}
                    {t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.uploaded-title-suffix'
                    )}
                </div>
                <div className="grid grid-cols-6 w-full flex-wrap gap-3">
                    {Array.from(files.entries()).map(([hash, item]) => (
                        <FileCard
                            key={hash}
                            file={item.file}
                            displayName={
                                isDynamicUrlCreative(item.properties)
                                    ? item.properties.name
                                    : undefined
                            }
                            properties={item.properties}
                            previewUrl={
                                item.properties.type === 'video'
                                    ? item.localPreviewUrl || item.remoteUrl
                                    : item.remoteUrl || item.localPreviewUrl
                            }
                            processing={item.upload.status === 'processing'}
                            waitingForProcessing={
                                item.upload.status === 'waiting'
                            }
                            uploadStatus={item.upload.status}
                            error={item.upload.message}
                            onRetry={
                                item.upload.status === 'error'
                                    ? retryItem
                                    : undefined
                            }
                            assignedCount={
                                assignmentCountByCreativeHash.get(hash) ?? 0
                            }
                            compatibleGroups={
                                compatibleGroupsByCreativeHash.get(hash) ?? []
                            }
                            onReassignToGroup={
                                item.creativeId
                                    ? toggleCreativeInGroups
                                    : undefined
                            }
                            onReassignAuto={
                                item.creativeId
                                    ? assignCreativeToAllGroups
                                    : undefined
                            }
                            onRemove={() => removeFile(hash)}
                        />
                    ))}
                </div>
                <div className="title text-xl font-extralight">
                    {t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.tags-groups'
                    )}
                    <p className="text-gray-500 mt-2 font-normal text-sm">
                        <b>
                            {t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.auto-assignment-label'
                            )}{' '}
                        </b>
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.ui.auto-assignment-description'
                        )}
                        <RequiredTag
                            alert={
                                touched.groups &&
                                (errors.groups ||
                                    errors.links ||
                                    errors.screens)
                            }
                        />
                    </p>
                    {validationMessages.length > 0 && (
                        <div className="mt-3 rounded-md border border-yellow-200 bg-yellow-100 px-3 py-2 text-sm text-yellow-900 space-y-1">
                            {validationMessages.map((message) => (
                                <p key={message} className="text-yellow-900">
                                    {message}
                                </p>
                            ))}
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-4 2xl:grid-cols-5 w-full flex-wrap gap-2 columns-4 2xl:columns-5">
                    {screensGroups?.map((screen) => (
                        <GroupContainer
                            key={screen.hash}
                            groupHash={screen.hash}
                            rotation={screen?.screens[0].rotation}
                            width={screen?.screens[0].width}
                            height={screen?.screens[0].height}
                            slot_duration={screen?.screens[0].slot_duration}
                            motion_type={screen?.screens[0].motion_type}
                            player={screen?.screens[0].player}
                            maxImageWeightBytes={screen.max_image_weight_bytes}
                            maxVideoWeightBytes={screen.max_video_weight_bytes}
                            assigned_creatives={screen?.assigned_creatives}
                            screen_count={screen?.screens.length}
                            screens={screen.screens}
                            hasError={
                                touched.groups &&
                                errors.groups?.emptyGroupHashes?.includes(
                                    screen.hash
                                )
                            }
                            compatibleCreatives={Array.from(files.values())
                                .filter((creative) => {
                                    const creativeHash =
                                        creative?.properties?.hash;

                                    if (!creativeHash) {
                                        return false;
                                    }

                                    if (!creative.creativeId) {
                                        return false;
                                    }

                                    if (
                                        (screen.assigned_creatives ?? []).some(
                                            (assignedCreative) =>
                                                assignedCreative.properties
                                                    ?.hash === creativeHash
                                        )
                                    ) {
                                        return false;
                                    }

                                    return isCreativeCompatibleWithGroup(
                                        screen,
                                        creative.properties
                                    );
                                })
                                .map((creative) => ({
                                    ...creative,
                                    displayName: getCreativeDisplayName(
                                        creative,
                                        t
                                    ),
                                }))}
                            onAssignCreative={assignCreativeToGroup}
                            canAssignCreatives={!hasUnsavedSelectedScreens}
                            onUnlink={(groupHash, hash) => {
                                UnassignCreativeFromGroup(groupHash, hash);
                            }}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
});

export default CreativesContainer;
